import { clsx } from 'clsx';
import type { CSSProperties } from 'react';

import { FieldEdit } from '@/App/tiles/Option/strategy/FieldEdit.tsx';
import { getFieldRow, getFieldSpan, isHedgeField } from '@/App/tiles/Option/strategy/strategyLayout.ts';
import type { StrategyField, StrategyFields, StrategyOrientation } from '@/App/tiles/Option/strategy/types.ts';
import type { LegsData, OptionLegProperties } from '@/store/option/state.ts';

type StrategyLegFieldsProps = {
  tileId: string;
  fields: StrategyFields;
  mode: StrategyOrientation;
  leg: OptionLegProperties;
  quoteLeg?: LegsData;
  column?: number;
  sharedHedges?: boolean;
};

export function StrategyLegFields(props: StrategyLegFieldsProps) {
  const { tileId, fields, mode, leg, quoteLeg, column, sharedHedges } = props;
  const displayedFields = mode === 'horizontal' ? fields.flat() : fields;

  return displayedFields.map((fieldOrGroup, index) => {
    // Leaving the column undefined preserves the original ungrouped auto-placement.
    const style: CSSProperties | undefined =
      mode === 'horizontal' || column === undefined
        ? undefined
        : { gridColumn: column, gridRow: getFieldRow(fields, index) };

    if (!Array.isArray(fieldOrGroup)) {
      return (
        <StrategyLegFieldCell
          key={fieldOrGroup}
          tileId={tileId}
          field={fieldOrGroup}
          leg={leg}
          quoteLeg={quoteLeg}
          sharedHedges={sharedHedges}
          style={style}
        />
      );
    }

    // Invisible hedge cells retain their slots; hiding the wrapper also removes per-leg dividers.
    return (
      <div
        key={`group-${fieldOrGroup[0]}`}
        style={{ ...style, gridRowEnd: `span ${getFieldSpan(fieldOrGroup)}` }}
        className={clsx('fieldGroup', {
          'pricing-fields': fieldOrGroup.includes('premium'),
          invisible: sharedHedges && fieldOrGroup.every(isHedgeField),
        })}
      >
        {fieldOrGroup.map((field) => (
          <StrategyLegFieldCell
            key={field}
            tileId={tileId}
            field={field}
            leg={leg}
            quoteLeg={quoteLeg}
            sharedHedges={sharedHedges}
          />
        ))}
      </div>
    );
  });
}

type StrategyLegFieldCellProps = Pick<StrategyLegFieldsProps, 'tileId' | 'leg' | 'quoteLeg' | 'sharedHedges'> & {
  field: StrategyField;
  style?: CSSProperties;
};

function StrategyLegFieldCell({ tileId, field, style, leg, quoteLeg, sharedHedges }: StrategyLegFieldCellProps) {
  if (sharedHedges && isHedgeField(field)) {
    return <div className="grid-cell invisible" />;
  }

  return (
    <FieldEdit visible field={field} leg={leg} quoteLeg={quoteLeg} legId={leg.legId} tileId={tileId} style={style} />
  );
}
